import React from 'react';
import type { OwaHeaderProps } from 'owa-bootstrap';
import { isCapabilityEnabled } from 'owa-capabilities';
import { novaMeControlCapability } from 'owa-capabilities-definitions/lib/novaMeControlCapability';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FluentNovaMeControl, FluentNovaMeControlContainer } from 'owa-fluent-nova-mecontrol';
import { MergedNotificationPane } from 'owa-header-charms';
import { PendingAccountsWelcomeCallout as LazyPendingAccountsWelcomeCallout } from 'owa-mail-accounts-view-callout';
import { observer } from 'owa-mobx-react';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { LazyOwaMonarchReferralCampaign } from 'owa-new-outlook-promo-dialog';
import { LazyPstTeachingCallout } from 'owa-pst-teaching-callout';
import {
    RepromptDialog as LazyRepromptDialog,
    RepromptDialogV2 as LazyRepromptDialogV2,
} from 'owa-reprompt-dialog';
import { HEADER_RESPONSIVE_BREAKPOINT } from '../constants/constants';
import useHeaderResponsive from '../hooks/useHeaderResponsive';
import OwaHeaderButtonGroup from './buttons/OwaHeaderButtonGroup';
import OwaHeaderBrandingLabel from './charms/OwaHeaderBrandingLabel';
import OwaHeaderDevSourceLabel from './charms/OwaHeaderDevSourceLabel';
import {
    headerLeft,
    headerRight,
    headerCenter,
    headerCharmsAndSearch,
    headerLeftCharms,
    headerSearchScopePicker,
    headerSearchBox,
} from './OwaHeader.scss';
import OwaHeaderBackground from './OwaHeaderBackground';

export default observer(function OwaHeader(props: OwaHeaderProps) {
    const charmsAndSearchRef = useHeaderResponsive(HEADER_RESPONSIVE_BREAKPOINT, {
        searchBoxRespond: props.searchBoxRespond,
        currentModule: props.currentModule,
    });

    const header = (
        <OwaHeaderBackground>
            <div className={headerLeft} />
            <div className={headerCenter}>
                <div className={headerCharmsAndSearch} ref={charmsAndSearchRef}>
                    <div className={headerLeftCharms}>
                        <OwaHeaderDevSourceLabel />
                        <OwaHeaderBrandingLabel />
                        <div className={headerSearchScopePicker}>
                            {props.renderSearchScopePicker?.()}
                        </div>
                    </div>
                    <div className={headerSearchBox}>{props.renderSearch?.()}</div>
                    <OwaHeaderButtonGroup />
                </div>
            </div>
            <div className={headerRight} />
        </OwaHeaderBackground>
    );

    return (
        <>
            {isFeatureEnabled('acct-owaMonarchReferral') && <LazyOwaMonarchReferralCampaign />}
            {isFeatureEnabled('acct-pstFileSupport') && <LazyPstTeachingCallout />}
            {isFeatureEnabled('settings-repromptDialog-v2') ? (
                <LazyRepromptDialogV2 />
            ) : (
                <LazyRepromptDialog />
            )}
            <LazyPendingAccountsWelcomeCallout />
            <MergedNotificationPane key="notificationPane" />
            {getWrappedHeader(header, props)}
        </>
    );
}, 'OwaHeader');

function getWrappedHeader(header: React.ReactElement, props: OwaHeaderProps): React.ReactNode {
    const novaMeControl = isCapabilityEnabled(novaMeControlCapability)
        ? (FluentNovaMeControl as React.FC<{
              accountKey: string;
          }>)
        : undefined;

    // Conditionally wrap the header with the MeControl container or custom wrapper
    if (props.wrapSuiteHeader) {
        return props.wrapSuiteHeader(header, novaMeControl);
    } else if (novaMeControl) {
        return (
            <FluentNovaMeControlContainer
                accountKey={getAccountKeyForMailboxInfo(getModuleContextMailboxInfo())}
            >
                {header}
            </FluentNovaMeControlContainer>
        );
    } else {
        return header;
    }
}
